import { ChevronDownIcon, ChevronLeftIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import watch, { WatchResponse } from "@src/api/watch/watch";
import TabBodies from "@src/components/elements/shared/tab/TabBodies";
import TabBody from "@src/components/elements/shared/tab/TabBody";
import TabController from "@src/components/elements/shared/tab/TabController";
import TabHeader from "@src/components/elements/shared/tab/TabHeader";
import TabHeaders from "@src/components/elements/shared/tab/TabHeaders";
import AppPromoPopup from "@src/components/popup/AppPopup";
import { useInfiniteQueryOnScroll } from "@src/hooks/useInfiniteQueryOnScroll";
import Watch from "@src/pages/watch/Watch";
import { useAppSelector } from "@src/state/hooks";
import usePopupModal from "@src/state/modal/usePopupModal";
import isMobile from "@src/utils/isMobile";

type Props = {
  tab?: string;
};

const WatchContainer = ({ tab = "toYou" }: Props) => {
  const { user } = useAppSelector(state => state.user);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const featured = searchParams.get("featured");
  const [muted, setMuted] = useState(true);
  const [activePost, setActivePost] = useState<number>(0);
  const queryParams = {
    featured_media_id: featured,
    user_id: user && tab === "myWatch" ? user.id : undefined,
    following: tab === "following",
    sort_by: "random",
  };
  const scrollRef = useRef<HTMLDivElement>(null);

  const { result: watchResult, handleScroll } = useInfiniteQueryOnScroll(
    {
      queryKey: ["watch", "posts", tab],
      queryFn: ({ pageParam }) =>
        watch({
          params: {
            ...queryParams,
            page: pageParam,
          },
        }),
      initialPageParam: 1,
      getNextPageParam: lastPage => {
        const nextPage = lastPage.current_page + 1;
        return nextPage <= lastPage.total_pages ? nextPage : null;
      },
    },
    { selector: null, offset: 10000 },
  );

  const processPosts = (posts: WatchResponse[]) => {
    if (!posts) return [];
    let allPosts = posts.flatMap(item => item.data);
    if (featured) {
      const featuredIndex = allPosts.findIndex(post => post.media[0].id === featured);
      if (featuredIndex > -1) {
        const [featuredPost] = allPosts.splice(featuredIndex, 1);
        allPosts = [featuredPost, ...allPosts];
      }
    }
    return allPosts;
  };
  const popupModal = usePopupModal();

  if (!watchResult.isSuccess) return <></>;

  const container = (
    <div
      className="mx-auto grid h-[calc(100dvh-40px)] w-screen snap-y snap-mandatory grid-cols-1 gap-2 overflow-y-scroll scrollbar-none md:h-[calc(100vh-200px)] md:w-[calc((100dvh-130px)/16*9)] xl:h-[calc(100vh-130px)]"
      onScroll={e => {
        const currentPost = Math.round(e.currentTarget.scrollTop / e.currentTarget.clientHeight);
        if (currentPost != activePost) {
          setActivePost(currentPost);
          handleScroll(e.nativeEvent);
        }
        if (isMobile) popupModal.open(<AppPromoPopup onClose={() => popupModal.close()} />);
      }}
      ref={scrollRef}
    >
      {processPosts(watchResult.data.pages).map((watch, i) => {
        return (
          <Watch
            queryKey={["watch", "posts", tab]}
            key={`watch:-${watch.media[0].id}`}
            data={watch}
            active={i === activePost}
            preload={activePost + 3 > i && activePost - 3 < i}
            autoplay={true}
            muted={muted}
            setMuted={(state: boolean) => setMuted(state)}
            scrollRef={scrollRef}
          />
        );
      })}
    </div>
  );
  const scrollToPost = (index: number) => {
    if (scrollRef.current) {
      const postHeight = scrollRef.current.clientHeight;
      scrollRef.current.scrollTo({
        top: index * postHeight,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="w-screen md:mx-auto md:w-fit">
      <TabController defaultTab={tab}>
        <TabHeaders
          center
          headerCSS="max-xl:w-full w-[80%] max-md:absolute left-0 max-md:z-[10] max-md:bg-opacity-40 max-md:brightness-125 max-md:px-4 gap-3 pb-4"
          tabCSS="max-md:invert max-md:grayscale"
        >
          <TabHeader id="back" url="/news/" hideDesktop>
            <ChevronLeftIcon className="size-5 text-gray-600" />
          </TabHeader>
          <TabHeader id="following" url="/watch/following">
            {t("main.watchTabs.following")}
          </TabHeader>
          <TabHeader id="toYou" url="/watch/für_dich">
            {t("main.watchTabs.toYou")}
          </TabHeader>
        </TabHeaders>
        <TabBodies>
          <TabBody id="myWatch">{container}</TabBody>
          <TabBody id="toYou">{container}</TabBody>
          <TabBody id="following">{container}</TabBody>
          <TabBody id="trending">{container}</TabBody>
        </TabBodies>
      </TabController>
      {/* Navigation Buttons */}
      <div className="absolute right-4 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-3">
        <button
          onClick={() => scrollToPost(Math.max(activePost - 1, 0))}
          className="flex size-10 items-center justify-center rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70"
        >
          <ChevronUpIcon className="size-5" />
        </button>
        <button
          onClick={() =>
            scrollToPost(Math.min(activePost + 1, processPosts(watchResult.data.pages).length - 1))
          }
          className="flex size-10 items-center justify-center rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70"
        >
          <ChevronDownIcon className="size-5" />
        </button>
      </div>
    </div>
  );
};

export default WatchContainer;
